import React from "react"
import { Box, Divider, Grid, Paper, Stack, Typography } from "@mui/material"
import TargetIcon from "@mui/icons-material/Adjust" // Замена для Scoring
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import GppBadIcon from "@mui/icons-material/GppBad" // Замена для Fair Play / Security
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined" // Настройка темной темы Material UI под дизайн игры

export default function Rules() {
  const zombiePoints = [
    { name: "COMMON ZOMBIE", pts: 10, color: "#94a3b8" },
    { name: "UNCOMMON ZOMBIE", pts: 25, color: "#22c55e" },
    { name: "RARE ZOMBIE", pts: 50, color: "#3b82f6" },
    { name: "EPIC ZOMBIE", pts: 100, color: "#a855f7" },
    { name: "LEGENDARY ZOMBIE", pts: 200, color: "#eab308" },
  ]

  return (
    <Box>
      <Box sx={{ mb: 5 }}>
        <Typography
          component="h2"
          sx={{
            fontWeight: "bold",
            mb: 0.5,
            textTransform: "uppercase",
            fontSize: 32,
          }}
        >
          Tournament Rules
        </Typography>
        <Typography>
          Everything you need to know about scoring, leaderboard and fair play.
        </Typography>
      </Box>

      {/* CONTENT SECTIONS GRID */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {/* ROW 1: Scoring & Zombie Points */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
            >
              <TargetIcon sx={{ fontSize: 40, color: "primary.main" }} />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", textTransform: "uppercase" }}
              >
                Scoring
              </Typography>
            </Box>

            <Stack spacing={1} sx={{ pl: 5, mb: 2 }}>
              <ul className="customList">
                <li>Points are awarded for killing zombies.</li>
                <li>
                  Different zombie types grant different amounts of points.
                </li>
                <li>Your score is the total points earned in a single run.</li>
                <li>
                  The higher the score, the higher your place on the
                  leaderboard.
                </li>
              </ul>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderColor: "#18181b",
                bgcolor: "rgba(9, 9, 11, 0.4)",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", textTransform: "uppercase", mb: 2 }}
              >
                Zombie Points
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {zombiePoints.map((zombie, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontWeight: "bold",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          bgcolor: zombie.color,
                          mx: 1,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: "#dedede", fontWeight: "bold" }}
                      >
                        {zombie.name}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        flexGrow: 1,
                        mx: 2,
                        height: "12px",
                        backgroundImage:
                          "linear-gradient(to right, #333 2px, transparent 2px)",
                        backgroundSize: "12px 2px",
                        backgroundRepeat: "repeat-x",
                        backgroundPosition: "bottom",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: "#c49654", fontWeight: "bold" }}
                    >
                      {zombie.pts} PTS
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: "#18181b" }} />

        {/* ROW 2: Leaderboard & Fair Play */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
            >
              <TrendingUpIcon sx={{ fontSize: 40, color: "primary.main" }} />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", textTransform: "uppercase" }}
              >
                Leaderboard
              </Typography>
            </Box>

            <Stack spacing={1} sx={{ pl: 5, mb: 2 }}>
              <ul className="customList">
                <li>Only your best score during the tournament is recorded.</li>
                <li>
                  You can play multiple times during the tournament period.
                </li>
                <li>
                  If you improve your score, the leaderboard will update
                  automatically.
                </li>
                <li>If scores are equal, the earlier result ranks higher.</li>
              </ul>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
            >
              <GppBadIcon sx={{ fontSize: 40, color: "primary.main" }} />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", textTransform: "uppercase" }}
              >
                Fair Play & Security
              </Typography>
            </Box>

            <Stack spacing={1} sx={{ pl: 5, mb: 2 }}>
              <ul className="customList">
                <li>All tournament matches are validated on the server.</li>
                <li>
                  Anti-cheat systems monitor suspicious activity and score
                  manipulation.
                </li>
                <li>
                  The use of bots, exploits, modified clients, scripts, or
                  unauthorized software is strictly prohibited.
                </li>
                <li>
                  Violating the rules may result in score removal,
                  disqualification, or account suspension.
                </li>
              </ul>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: "#18181b" }} />

        {/* ROW 3: Rewards & Results */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <CardGiftcardIcon sx={{ fontSize: 40, color: "primary.main" }} />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "bold", textTransform: "uppercase" }}
            >
              Rewards & Results
            </Typography>
          </Box>

          <Stack spacing={1} sx={{ pl: 5, mb: 2 }}>
            <ul className="customList">
              <li>Final rankings are determined when the tournament ends.</li>
              <li>
                Rewards are distributed based on the final leaderboard
                positions.
              </li>
              <li>Winnings are automatically credited to your balance.</li>
              <li>
                Reward distribution may take a few minutes after the tournament
                ends.
              </li>
            </ul>
          </Stack>
        </Box>

        {/* IMPORTANT FOOTNOTE BOX */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mt: 3,
            py: 1,
            borderRadius: 1,
            border: "1px solid rgba(196,150,84,0.2)",
            background: "linear-gradient(180deg, #000 60%, #121212 100%)",
          }}
        >
          <ErrorOutlineOutlinedIcon
            sx={{ fontSize: 44, color: "#c49654", mx: 1.5 }}
          />
          <Box>
            <Typography
              sx={{
                display: "block",
                textTransform: "uppercase",
                fontWeight: "bold",
                mb: 0.5,
              }}
            >
              Important
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Only your highest score counts.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Play, improve, and climb the leaderboard to win{" "}
                USDT rewards!
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
